import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider, useTheme } from 'emotion-theming';
import { useContext, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import Input from '@veeva/corgix/input';
import Icon from '@veeva/corgix/icon';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import OverlayContext from '../../../common/containers/OverlayContext';
import { BREAKPOINTS } from '../../../common/services/breakpoints';
import {
    navigationBarSearchMobileCloseButtonCSS,
    navigationBarSearchMobileSearchBarCSS,
    navigationBarSearchMobileSearchInputCSS,
    searchInputMobileMenuCSS,
    searchInputMobileOverlayWidthCSS,
} from './styles/RevStatsSearchInputMobile.styles';
import useGetRevSearchResults from '../../services/data/useGetRevSearchResults';
import RevStatsSearchResultsMenu from './RevStatsSearchResultsMenu';

export default function RevStatsSearchInputMobile() {
    const navigate = useNavigate();
    const { fontSizes } = useTheme();
    const menuMapRef = useRef(null);
    const inputRef = useRef(null);
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get('q');
    const { setOverlay } = useContext(OverlayContext);
    const [searchText, setSearchText] = useState(searchQuery ?? '');
    const [filteredProducts, filteredCustomers] = useGetRevSearchResults(searchText);
    const [focusedNodeKeyValue, setFocusedNodeKeyValue] = useState(undefined);
    const isAboveSizeThreshold = useMediaQuery({ minWidth: BREAKPOINTS.SMALL.min });

    useEffect(() => {
        setSearchText(searchQuery ?? '');
    }, [searchQuery]);

    useEffect(() => {
        inputRef.current.focus();
    }, []);

    useEffect(() => {
        if (isAboveSizeThreshold) {
            setOverlay(null);
        }
    }, [isAboveSizeThreshold, setOverlay]);

    const handleCloseSearchOverlay = () => setOverlay(null);

    const handleIconClick = () => inputRef.current?.focus();

    const handleSearchInputChange = (e) => {
        setSearchText(e.target.value);
    };

    const handleKeyDown = async (e) => {
        switch (e.key) {
            case 'ArrowDown': {
                const newNode =
                    focusedNodeKeyValue === undefined
                        ? menuMapRef.current.getFirstNode()
                        : menuMapRef.current.getNextNode(focusedNodeKeyValue);
                if (newNode) {
                    setFocusedNodeKeyValue(newNode.keyValue);
                }
                break;
            }
            case 'ArrowUp': {
                const newNode =
                    focusedNodeKeyValue === undefined
                        ? menuMapRef.current.getLastNode()
                        : menuMapRef.current.getPreviousNode(focusedNodeKeyValue);
                if (newNode) {
                    setFocusedNodeKeyValue(newNode.keyValue);
                }
                break;
            }
            case 'Enter': {
                if (focusedNodeKeyValue === 'not-found') {
                    break;
                }
                if (focusedNodeKeyValue) {
                    navigate(focusedNodeKeyValue);
                } else {
                    const updatedSearchText = searchText.replace('&', '%26').replace('#', '%23');
                    navigate(`/search?q=${updatedSearchText}`);
                }
                handleCloseSearchOverlay();
                break;
            }
            default:
                break;
        }
    };

    const handleMenuMapUpdate = (menuMap) => {
        if (focusedNodeKeyValue !== undefined && !menuMap.hasValue(focusedNodeKeyValue)) {
            const firstKeyValue = menuMap.getFirstNode().keyValue;
            if (firstKeyValue === 'not-found') {
                setFocusedNodeKeyValue(undefined);
            } else {
                // If we had a focus item and it's no longer available, set it to the first node...unless it's the not-found item
                setFocusedNodeKeyValue(menuMap.getFirstNode().keyValue);
            }
        }
        menuMapRef.current = menuMap;
    };

    return (
        <ThemeProvider
            theme={{
                textInputFontSize: fontSizes.small,
                inputWidthMD: '25rem',
                textInputWidthMD: '100%',
                textInputHeight: null, // Let the height fill the container
                iconSizeMD: '1.25rem',
            }}
        >
            <div css={navigationBarSearchMobileSearchBarCSS}>
                <Input
                    inputRef={inputRef}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleKeyDown}
                    css={navigationBarSearchMobileSearchInputCSS}
                    placeholder="Search"
                    value={searchText}
                >
                    <Icon
                        onClick={handleIconClick}
                        size="md"
                        className="prepend"
                        type={faSearch}
                        title="Search"
                    />
                </Input>
                <button
                    css={navigationBarSearchMobileCloseButtonCSS}
                    onClick={handleCloseSearchOverlay}
                    type="button"
                >
                    Close
                </button>
            </div>
            <RevStatsSearchResultsMenu
                css={[searchInputMobileMenuCSS, searchInputMobileOverlayWidthCSS]}
                customers={filteredCustomers?.slice(0, 5)}
                products={filteredProducts?.slice(0, 5)}
                focusedValue={focusedNodeKeyValue || ''}
                onMenuItemClick={handleCloseSearchOverlay}
                onMenuMapUpdate={handleMenuMapUpdate}
                searchText={searchText}
            />
        </ThemeProvider>
    );
}
